import React from 'react';
import { FiMic, FiMicOff, FiX, FiStar } from 'react-icons/fi';
import './Modals.css';
import { useLanguage } from '../../context/LanguageContext';
import EvaluationSummary from '../Evaluation/EvaluationSummary';
import { useDialogFocus } from '../../hooks/useDialogFocus';

const EvaluationModal = ({
  isOpen,
  onClose,
  isEvaluating,
  diagnosis,
  managementPlan,
  evaluationResult,
  onDiagnosisChange,
  onManagementPlanChange,
  onSubmitEvaluation,
  onStartNewCase,
  onLeaveEncounter,
  canClose = true,
  assessmentPhase = false,
  timeRemaining,
  isTimeExpired = false,
  isChatPending = false,
  acceptedTranscript = []
}) => {
  const { language, t } = useLanguage();
  const [isListening, setIsListening] = React.useState(false);
  const recognitionRef = React.useRef(null);
  const titleId = React.useId();
  const diagnosisRef = React.useRef(null);
  const transcriptRef = React.useRef(null);
  const waitingForResponse = React.useRef(false);
  const [reviewOpen, setReviewOpen] = React.useState(false);
  const dialogRef = useDialogFocus(isOpen, { onClose, canClose: canClose && !isEvaluating, initialFocusRef: diagnosisRef });

  React.useEffect(() => {
    if (!isOpen) {
      waitingForResponse.current = false;
      setReviewOpen(false);
    } else if (isChatPending) {
      waitingForResponse.current = true;
    } else if (waitingForResponse.current) {
      waitingForResponse.current = false;
      setReviewOpen(true);
    }
  }, [isOpen, isChatPending]);

  React.useEffect(() => {
    if (reviewOpen && transcriptRef.current) transcriptRef.current.scrollTop = transcriptRef.current.scrollHeight;
  }, [reviewOpen, acceptedTranscript]);

  React.useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = true;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = { en: 'en-US', ru: 'ru-RU', uz: 'uz-UZ' }[language] || 'en-US';

      recognitionRef.current.onresult = (event) => {
        let transcript = '';
        for (let i = event.resultIndex; i < event.results.length; i++) {
          if (event.results[i].isFinal) {
            transcript += event.results[i][0].transcript + ' ';
          }
        }
        if (transcript) {
          onManagementPlanChange(prev => (prev || '') + transcript);
        }
      };

      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
      };
    }
  }, [onManagementPlanChange, language]);

  const toggleListening = () => {
    if (!recognitionRef.current) {
      alert(t('speechUnsupported'));
      return;
    }

    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      try {
        recognitionRef.current.start();
        setIsListening(true);
      } catch (error) {
        console.error("Failed to start speech recognition:", error);
        setIsListening(false);
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal evaluation-modal" ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby={titleId} aria-busy={Boolean(isEvaluating)} tabIndex={-1}>
        <div className="modal-header">
          <h2 id={titleId}>{t(evaluationResult ? 'clinicalEvaluation' : 'submitDiagnosis')}</h2>
          {canClose && <button className="close-modal" onClick={onClose} disabled={isEvaluating} aria-label={t('cancel')}>
            <FiX size={24} />
          </button>}
        </div>

        {!evaluationResult && timeRemaining !== undefined && <div className={`evaluation-session-clock${isTimeExpired ? ' expired' : ''}`}>
          <span>{t('assessmentTimeRemaining')}</span>
          <strong role="timer" aria-label={t('assessmentTimeRemaining')}>{timeRemaining}</strong>
        </div>}

        <div className="modal-body">
          {isEvaluating ? (
            <div className="evaluation-loading">
              <div className="loading-spinner"></div>
              <h3>{t('evaluationProgress')}</h3>
              <p>{t('aiAnalyzing')}</p>
              <div className="loading-steps">
                <div className="loading-step">✓ {t('analyzingDiagnosis')}</div>
                <div className="loading-step">✓ {t('reviewingPlan')}</div>
                <div className="loading-step active">{t('generatingFeedback')}</div>
              </div>
            </div>
          ) : evaluationResult ? (
            <div className="evaluation-result">
              <EvaluationSummary evaluation={evaluationResult} />
              <div className="modal-actions">
                <button className="btn-primary" onClick={onStartNewCase || onClose}>
                  {t('chooseAnother')}
                </button>
                <button className="btn-secondary" onClick={onClose}>
                  {t('cancel')}
                </button>
              </div>
            </div>
          ) : (
            <>
              {assessmentPhase && <p className="assessment-stage-help">{t('assessmentStageHelp')}</p>}
              {isTimeExpired && <p role="status" className="assessment-stage-help">{t('timeUpMessage')}</p>}
              {isChatPending && <p role="status" className="assessment-stage-help">{t('assessmentWaitingForResponse')}</p>}
              {acceptedTranscript.length > 0 && <details className="assessment-evidence" open={reviewOpen} onToggle={(event) => setReviewOpen(event.currentTarget.open)}>
                <summary>{t('reviewEncounterTranscript')}</summary>
                <div className="assessment-transcript" ref={transcriptRef} role="region" aria-label={t('fullTranscript')} tabIndex={0}>
                  {acceptedTranscript.map((message, index) => <div className="assessment-transcript-message" key={index}>
                    <strong>{message.role === 'user' ? t('youStudent') : message.role === 'patient' ? t('patient') : t('clinicalSystem')}</strong>
                    <p>{message.content}</p>
                  </div>)}
                </div>
              </details>}
              <div className="form-section">
                <label htmlFor={`${titleId}-diagnosis`}>{t('yourDiagnosis')}:</label>
                <textarea
                  id={`${titleId}-diagnosis`}
                  ref={diagnosisRef}
                  value={diagnosis}
                  onChange={(e) => onDiagnosisChange(e.target.value)}
                  placeholder={t('diagnosisPlaceholder')}
                  rows="3"
                  className="diagnosis-input"
                />
              </div>

              <div className="form-section">
                <label htmlFor={`${titleId}-plan`}>{t('managementPlan')}:</label>
                <textarea
                  id={`${titleId}-plan`}
                  value={managementPlan}
                  onChange={(e) => onManagementPlanChange(e.target.value)}
                  placeholder={t('planPlaceholder')}
                  rows="4"
                  className="management-input"
                />
                <button
                  type="button"
                  className={`mic-btn-floating ${isListening ? 'listening' : ''}`}
                  onClick={toggleListening}
                  title={isListening ? t('stopVoice') : t('voiceInput')}
                >
                  {isListening ? <><FiMicOff size={16} /> {t('stopVoice')}</> : <><FiMic size={16} /> {t('voiceInput')}</>}
                </button>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-primary submit-btn"
                  onClick={onSubmitEvaluation}
                  disabled={!diagnosis.trim() || !managementPlan.trim() || isEvaluating || isChatPending}
                >
                  <FiStar size={18} />
                  <span>{t('submitEvaluation')}</span>
                </button>
                {canClose && <button
                  type="button"
                  className="btn-secondary"
                  onClick={onClose}
                >
                  {t('cancel')}
                </button>}
                {!canClose && onLeaveEncounter && <button type="button" className="btn-secondary" onClick={onLeaveEncounter}>
                  {t('backToCases')}
                </button>}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default EvaluationModal;
